import { useEffect, useRef, useState } from 'react';
import { useAuth } from '../../hooks/useAuth';
import { ordersApi, AdminOrderDetailResponse, RescheduleRequest } from '../../services/ordersApi';
import { dubaiToday } from '../../types/providerSettlements';
import RescheduleFields, { RescheduleForm } from './RescheduleFields';
import RescheduleConsequence from './RescheduleConsequence';
import { RescheduleError, toRescheduleError } from './rescheduleErrors';
import { useRescheduleOptions } from './useRescheduleOptions';

interface Props {
  order: AdminOrderDetailResponse;
  /** The fresh order the server answered with. */
  onSuccess: (updated: AdminOrderDetailResponse) => void;
  /** 409 ORDER_CHANGED: the page tells the admin and reloads the order. */
  onConflict: () => void;
  onClose: () => void;
}

const NOTE_MAX = 500;

/** Spec 077 (#1218) — admin reschedules the pickup (before collection) or the return (car at the garage). */
export default function RescheduleOrderModal({ order, onSuccess, onConflict, onClose }: Props) {
  const auth = useAuth();
  const today = dubaiToday();
  const returnPhase = order.reschedulablePhase === 'RETURN';
  const [form, setForm] = useState<RescheduleForm>({
    date: order.scheduledDate ?? today,
    pickupSlotId: order.pickupTimeSlotId ?? '',
    returnDate: order.returnDate ?? '',
    returnSlotId: order.returnTimeSlotId ?? '',
    garageAgreed: false,
    note: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<RescheduleError | null>(null);
  const dialogRef = useRef<HTMLDivElement>(null);

  // Capacity is about the day the car arrives: the booking day, or the return day in the return phase.
  const lookupDate = returnPhase ? (form.returnDate || order.scheduledDate || today) : form.date;
  const { options, loading, error: loadError } = useRescheduleOptions(order.id, lookupDate, auth.token);

  // Escape closes; Tab stays inside the dialog.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !submitting) { onClose(); return; }
      if (e.key !== 'Tab' || !dialogRef.current) return;
      const items = dialogRef.current.querySelectorAll<HTMLElement>(
        'button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled])',
      );
      if (items.length === 0) return;
      const first = items[0];
      const last = items[items.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose, submitting]);

  const patch = (p: Partial<RescheduleForm>) => { setError(null); setForm((f) => ({ ...f, ...p })); };

  const editable = options?.editable;
  const full = options?.capacity?.full === true;
  const slotsMissing = (editable?.pickupSlot && !form.pickupSlotId) || (editable?.returnSlot && !form.returnSlotId);
  const dateMissing = editable?.date && !form.date;
  const canConfirm = !!options?.allowed && !loading && !submitting && !slotsMissing && !dateMissing
    && (!full || form.garageAgreed) && order.version != null;

  const handleConfirm = async () => {
    if (!auth.token || !editable || order.version == null) return;
    const body: RescheduleRequest = {
      date: editable.date ? form.date : undefined,
      pickupTimeSlotId: editable.pickupSlot ? form.pickupSlotId : undefined,
      returnDate: editable.returnDate && form.returnDate ? form.returnDate : undefined,
      returnTimeSlotId: editable.returnSlot ? form.returnSlotId : undefined,
      garageAgreed: full && form.garageAgreed,
      note: form.note.trim() || undefined,
      expectedVersion: order.version,
    };
    setError(null);
    setSubmitting(true);
    try {
      onSuccess(await ordersApi.rescheduleOrder(order.id, body, auth.token));
    } catch (err) {
      const mapped = toRescheduleError(err);
      if (mapped.conflict) { onConflict(); return; }
      setError(mapped);
      setSubmitting(false);
    }
  };

  return (
    <div className="admin-modal-overlay">
      <div
        ref={dialogRef}
        className="admin-modal"
        style={{ maxWidth: '480px', width: '100%' }}
        role="dialog"
        aria-modal="true"
        aria-labelledby="reschedule-title"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="admin-modal-header">
          <h3 id="reschedule-title" style={{ margin: 0 }}>Reschedule order</h3>
          <button className="admin-modal-close" onClick={onClose} disabled={submitting} aria-label="Close">&times;</button>
        </div>

        <div className="admin-modal-body">
          {loadError && <div className="admin-info-banner danger" role="alert">{loadError}</div>}
          {!options && loading && <p style={{ color: 'var(--admin-text-muted)' }}>Loading options…</p>}
          {options && !options.allowed && (
            <div className="admin-info-banner warning" role="alert">
              {options.reasonMessage ?? options.reason ?? "This order can't be rescheduled right now."}
            </div>
          )}
          {options?.allowed && (
            <>
              <div className="admin-info-banner info" data-testid="reschedule-phase">
                {options.phase === 'RETURN' ? 'Car at the garage — return only' : 'Car not collected yet'}
              </div>
              <RescheduleFields
                options={options}
                form={form}
                onChange={patch}
                today={today}
                error={error}
                checking={loading}
                disabled={submitting}
              />
              <RescheduleConsequence phase={options.phase} legs={order.pickupDeliveries} />
              <div className="admin-form-group">
                <label className="admin-form-label" htmlFor="reschedule-note">Internal note — not sent to anyone</label>
                <textarea
                  id="reschedule-note"
                  className="admin-textarea"
                  rows={3}
                  maxLength={NOTE_MAX}
                  value={form.note}
                  onChange={(e) => patch({ note: e.target.value })}
                  disabled={submitting}
                />
                <div style={{ fontSize: '0.75rem', color: 'var(--admin-text-muted)', marginTop: '0.25rem' }}>
                  {form.note.length}/{NOTE_MAX}
                </div>
              </div>
              {error && error.field === 'form' && (
                <div className="admin-info-banner danger" role="alert" data-testid="reschedule-error">{error.message}</div>
              )}
            </>
          )}
        </div>

        <div className="admin-modal-footer">
          <button className="admin-btn admin-btn-outline" onClick={onClose} disabled={submitting}>Cancel</button>
          <button
            className="admin-btn admin-btn-primary"
            data-testid="reschedule-confirm"
            onClick={handleConfirm}
            disabled={!canConfirm}
          >
            {submitting ? 'Rescheduling…' : 'Confirm reschedule'}
          </button>
        </div>
      </div>
    </div>
  );
}
